import { useMemo, useRef, useState } from "react";
import { Pencil, Pin } from "lucide-react";
import { toast } from "sonner";

// Numbers-first (names starting with a digit), then alphabetical, natural numeric order.
export const sortCustomers = (options) => {
  const nameOf = (c) => (c.company_name || c.name || "").trim();
  return [...options].sort((a, b) => {
    const an = nameOf(a), bn = nameOf(b);
    const ad = /^\d/.test(an), bd = /^\d/.test(bn);
    if (ad !== bd) return ad ? -1 : 1;
    return an.localeCompare(bn, undefined, { sensitivity: "base", numeric: true });
  });
};

// Per-device pick frequency — powers the "Often used" top-3 section.
const FREQ_KEY = "customer_pick_freq_v1";
const readFreq = () => {
  try { return JSON.parse(localStorage.getItem(FREQ_KEY) || "{}") || {}; } catch { return {}; }
};

// Per-device pinned customers (ordered array of customer_ids, newest pin first).
const PIN_KEY = "customer_pick_pinned_v1";
const readPinned = () => {
  try {
    const v = JSON.parse(localStorage.getItem(PIN_KEY) || "[]");
    return Array.isArray(v) ? v : [];
  } catch { return []; }
};

/**
 * Searchable customer picker. `dark` matches the dark dialogs (new project,
 * estimates); default light matches the edit-project dialog. `label` sets the
 * closed-state button text (e.g. the selected customer name).
 *
 * The panel is FIXED-positioned from the trigger's bounding rect and clamped
 * to the viewport, so it can never slide off the left/right edge on phones.
 *
 * Long-press (hold ~0.5s) any customer row to pin/unpin it — pinned customers
 * always sit at the very top of the list, ahead of the "Often used" section.
 */
export const CustomerPicker = ({ options, onPick, dark = false, label = "Pick…", testid = "customer-picker", onManual = null, wide = false }) => {
  const [open, setOpen] = useState(false);
  const [q, setQ] = useState("");
  const [freq, setFreq] = useState(readFreq);
  const [pinned, setPinned] = useState(readPinned);
  const [pos, setPos] = useState({ left: 8, top: 0, width: 256 });
  const btnRef = useRef(null);
  const pressTimer = useRef(null);
  const didLongPress = useRef(false);

  const sorted = useMemo(() => sortCustomers(options), [options]);
  const filtered = q.trim()
    ? sorted.filter((c) => (c.company_name || c.name || "").toLowerCase().includes(q.trim().toLowerCase()))
    : sorted;

  // Pinned customers that still exist, in pin order (newest pin first).
  const pinnedCustomers = useMemo(() => {
    const byId = new Map(options.map((o) => [o.customer_id, o]));
    return pinned.map((id) => byId.get(id)).filter(Boolean);
  }, [pinned, options]);
  const pinnedIds = useMemo(() => new Set(pinnedCustomers.map((c) => c.customer_id)), [pinnedCustomers]);

  // Top 3 most-picked customers that still exist — pinned ones are already
  // shown above, so they don't repeat here.
  const favorites = useMemo(() => {
    const byId = new Map(options.map((o) => [o.customer_id, o]));
    return Object.entries(freq)
      .sort((a, b) => b[1] - a[1])
      .map(([id]) => byId.get(id))
      .filter(Boolean)
      .filter((c) => !pinnedIds.has(c.customer_id))
      .slice(0, 3);
  }, [freq, options, pinnedIds]);

  const toggle = () => {
    if (!open) {
      // Re-read pick counts + pins on every open so sections are never stale.
      setFreq(readFreq());
      setPinned(readPinned());
      if (btnRef.current) {
        const r = btnRef.current.getBoundingClientRect();
        const w = Math.min(256, window.innerWidth - 16);
        // Prefer right-aligning the panel to the trigger (the dialog look), but
        // clamp so it never goes off the left OR right edge of the screen.
        const left = Math.max(8, Math.min(r.right - w, window.innerWidth - w - 8));
        const estH = 320; // search box + list cap + manual row
        const below = r.bottom + 4;
        // Flip above the trigger when there is no room below.
        const top = below + estH > window.innerHeight && r.top - estH - 4 > 8 ? r.top - estH - 4 : below;
        setPos({ left, top, width: w });
      }
    }
    setOpen((v) => !v);
    setQ("");
  };

  const pick = (c) => {
    const cur = readFreq();
    const next = { ...cur, [c.customer_id]: (cur[c.customer_id] || 0) + 1 };
    try { localStorage.setItem(FREQ_KEY, JSON.stringify(next)); } catch { /* ignore */ }
    setFreq(next);
    onPick(c);
    setOpen(false);
  };

  const togglePin = (c) => {
    const cur = readPinned();
    const name = c.company_name || c.name;
    let next;
    if (cur.includes(c.customer_id)) {
      next = cur.filter((id) => id !== c.customer_id);
      toast.info(`Unpinned ${name}`, { duration: 2000 });
    } else {
      next = [c.customer_id, ...cur];
      toast.success(`Pinned ${name} to the top`, { duration: 2000 });
    }
    try { localStorage.setItem(PIN_KEY, JSON.stringify(next)); } catch { /* ignore */ }
    setPinned(next);
  };

  // Long-press detection: hold ~0.5s toggles the pin instead of picking.
  const pressStart = (c) => {
    didLongPress.current = false;
    if (pressTimer.current) clearTimeout(pressTimer.current);
    pressTimer.current = setTimeout(() => {
      didLongPress.current = true;
      togglePin(c);
    }, 500);
  };
  const pressEnd = () => {
    if (pressTimer.current) { clearTimeout(pressTimer.current); pressTimer.current = null; }
  };
  const itemClick = (c) => {
    // Swallow the click that follows a long-press so it doesn't pick.
    if (didLongPress.current) { didLongPress.current = false; return; }
    pick(c);
  };

  const btnCls = dark
    ? "h-10 shrink-0 max-w-[110px] truncate rounded-md border border-white/15 bg-white/[0.03] px-2 text-sm text-white focus:border-[#1a3fd1] focus:outline-none"
    : `h-10 shrink-0 ${wide ? "max-w-[260px]" : "max-w-[140px]"} truncate rounded-md border border-gray-300 bg-white px-2 text-sm text-gray-800 focus:border-[#1a3fd1] focus:outline-none`;
  const panelCls = dark
    ? "fixed z-50 overflow-hidden rounded-md border border-white/15 bg-[#111] shadow-xl"
    : "fixed z-50 overflow-hidden rounded-md border border-gray-200 bg-white shadow-lg";
  const inputCls = dark
    ? "w-full rounded border border-white/15 bg-white/[0.03] px-2 py-1.5 text-sm text-white outline-none focus:border-[#1a3fd1]"
    : "w-full rounded border border-gray-300 px-2 py-1.5 text-sm text-gray-800 focus:border-[#1a3fd1] focus:outline-none";
  const itemCls = dark
    ? "flex w-full select-none items-center gap-1.5 truncate px-3 py-2 text-left text-sm text-white/80 hover:bg-[#1a3fd1]/20"
    : "flex w-full select-none items-center gap-1.5 truncate px-3 py-2 text-left text-sm text-gray-700 hover:bg-blue-50";
  const labelCls = dark
    ? "flex items-center justify-between px-3 pb-0.5 pt-1.5 font-mono text-[9px] uppercase tracking-[0.2em] text-white/35"
    : "flex items-center justify-between px-3 pb-0.5 pt-1.5 font-mono text-[9px] uppercase tracking-[0.2em] text-gray-400";

  const renderItem = (c, keyPrefix, tidPrefix) => {
    const isPinned = pinnedIds.has(c.customer_id);
    return (
      <li key={`${keyPrefix}-${c.customer_id}`}>
        <button
          type="button"
          onClick={() => itemClick(c)}
          onPointerDown={() => pressStart(c)}
          onPointerUp={pressEnd}
          onPointerLeave={pressEnd}
          onPointerCancel={pressEnd}
          onContextMenu={(e) => e.preventDefault()}
          data-testid={`${testid}-${tidPrefix}-${c.customer_id}`}
          className={itemCls}
        >
          {isPinned && <Pin className={`h-3 w-3 shrink-0 ${dark ? "text-amber-300" : "text-amber-500"}`} />}
          <span className="truncate">{c.company_name || c.name}</span>
        </button>
      </li>
    );
  };

  const favIds = new Set(favorites.map((f) => f.customer_id));
  const searching = !!q.trim();
  const showPinned = !searching && pinnedCustomers.length > 0;
  const showFavs = !searching && favorites.length > 0;

  return (
    <div className="relative shrink-0">
      <button
        ref={btnRef}
        type="button"
        onClick={toggle}
        data-testid={testid}
        className={btnCls}
      >
        {label}
      </button>
      {open && (
        <>
          <div className="fixed inset-0 z-40" onClick={() => setOpen(false)} />
          <div className={panelCls} style={{ left: pos.left, top: pos.top, width: pos.width }} data-testid={`${testid}-panel`}>
            <div className={`border-b p-2 ${dark ? "border-white/10" : "border-gray-100"}`}>
              <input
                autoFocus
                value={q}
                onChange={(e) => setQ(e.target.value)}
                placeholder="Search customers…"
                data-testid={`${testid}-search`}
                className={inputCls}
              />
            </div>
            <ul className="max-h-56 overflow-y-auto" data-testid={`${testid}-list`}>
              {filtered.length === 0 && (
                <li className={`px-3 py-3 text-center text-xs ${dark ? "text-white/40" : "text-gray-400"}`}>No matches</li>
              )}
              {showPinned && (
                <>
                  <li className={labelCls} data-testid={`${testid}-pinned`}>
                    <span>Pinned</span>
                    <span className="normal-case tracking-normal opacity-70">hold to unpin</span>
                  </li>
                  {pinnedCustomers.map((c) => renderItem(c, "pin", "pin"))}
                  <li aria-hidden="true" className={`mx-2 my-1 border-t ${dark ? "border-white/10" : "border-gray-100"}`} />
                </>
              )}
              {showFavs && (
                <>
                  <li className={labelCls} data-testid={`${testid}-favorites`}>
                    <span>Often used</span>
                    <span className="normal-case tracking-normal opacity-70">hold to pin</span>
                  </li>
                  {favorites.map((c) => renderItem(c, "fav", "fav"))}
                  <li aria-hidden="true" className={`mx-2 my-1 border-t ${dark ? "border-white/10" : "border-gray-100"}`} />
                </>
              )}
              {filtered.filter((c) => searching || (!pinnedIds.has(c.customer_id) && !favIds.has(c.customer_id))).map((c) => renderItem(c, "opt", "option"))}
            </ul>
            {onManual && (
              <div className={`border-t ${dark ? "border-white/10" : "border-gray-100"}`}>
                <button
                  type="button"
                  onClick={() => { onManual(); setOpen(false); }}
                  data-testid={`${testid}-manual`}
                  className={`flex w-full items-center gap-2 px-3 py-2 text-left text-sm font-semibold ${dark ? "text-emerald-300 hover:bg-white/5" : "text-[#1a3fd1] hover:bg-blue-50"}`}
                >
                  <Pencil className="h-3.5 w-3.5" /> Enter manually…
                </button>
              </div>
            )}
          </div>
        </>
      )}
    </div>
  );
};
